import "./header.css";
import React, { useState, useEffect, useRef } from "react";
import { ArrowLeftRight } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Logout } from "@mui/icons-material";
import ConfirmationDialog from "../../confirmation-dialog/confirmation-dialog";
import { useNavigate } from "react-router-dom";
import { logOut, restoreTrueRole, setRole } from "../../../redux/features/userSlice";
import { isViewingAs } from "../../../utils/viewAsHelper";

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const user = useSelector((state) => state.user);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const canUseRoleSwitcher =
    user?.trueRole === "Super Admin" || user?.trueRole === "Admin";
  const viewingAsAnother = isViewingAs(user);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleConfirmationModalOpen = () => {
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
  };

  const logout = () => {
    localStorage.removeItem("persist:root");
    dispatch(logOut());
    navigate("/");
  };

  const handleRoleChange = (newRole) => {
    dispatch(setRole(newRole));
    setIsDropdownOpen(false);
  };

  const handleRestoreTrueRole = () => {
    dispatch(restoreTrueRole());
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`header-wrapper ${isDropdownOpen ? "dropdown-open" : ""}`}>
      <header className="header">
        <div className="header-leading">
          <div className="logo-container">
            <img src="/Images/plus.png" alt="Logo" className="logo" />
            <div className="title">
              <div>HOSPITAL MANAGEMENT</div>
              <div>INFORMATION SYSTEM</div>
            </div>
          </div>
        </div>
        {canUseRoleSwitcher && viewingAsAnother && (
          <button
            type="button"
            className="header-preview-chip"
            onClick={handleRestoreTrueRole}
            aria-label={`Viewing as ${user.role}. Exit preview and return to ${user.trueRole}.`}
          >
            <span className="header-preview-chip__dot" aria-hidden />
            <span className="header-preview-chip__text">
              Viewing as <strong>{user.role}</strong>
            </span>
            <span className="header-preview-chip__exit">↩ Exit Preview</span>
          </button>
        )}
        <div className="user-actions">
          <div className="user-info">
            <img src="/Images/admin.png" alt="Admin" className="admin-image" />
            <div className="user-text">
              <span className="admin-name">{user.role}</span>
              <span className="email">{user.email}</span>
            </div>
          </div>
          {canUseRoleSwitcher && (
            <button type="button" className="icon-button1" onClick={toggleDropdown}>
              <ArrowLeftRight size={30} />
            </button>
          )}
          <button className="icon-button" onClick={handleConfirmationModalOpen}>
            <Logout size={25} />
          </button>
          <ConfirmationDialog
            title="Are you sure you want to logout?"
            open={confirmationModalOpen}
            onCancel={handleConfirmationModalClose}
            onConfirm={logout}
            isLoading={false}
          />
        </div>
      </header>
      {isDropdownOpen && canUseRoleSwitcher && (
        <div className="dropdown-menuu" ref={dropdownRef}>
          {viewingAsAnother && (
            <>
              <div
                className="dropdown-item dropdown-item--restore"
                onClick={handleRestoreTrueRole}
              >
                Exit preview — return to {user.trueRole}
              </div>
              <div className="dropdown-separator" />
            </>
          )}
          {user.trueRole === "Super Admin" && user.role !== "Super Admin" && (
            <>
              <div
                className="dropdown-item"
                onClick={() => handleRoleChange("Super Admin")}
              >
                Super Admin
              </div>
              <div className="dropdown-separator" />
            </>
          )}
          <div className="dropdown-item" onClick={() => handleRoleChange("Admin")}>Admin</div>
          <div className="dropdown-separator" />
          <div className="dropdown-item" onClick={() => handleRoleChange("Nurse")}>Nurse</div>
          <div className="dropdown-separator" />
          <div className="dropdown-item" onClick={() => handleRoleChange("Reception")}>Reception</div>
          <div className="dropdown-separator" />
          <div className="dropdown-item" onClick={() => handleRoleChange("HR")}>HR</div>
          <div className="dropdown-separator" />
          <div className="dropdown-item" onClick={() => handleRoleChange("HOD")}>HOD</div>
          <div className="dropdown-separator" />
          <div className="dropdown-item" onClick={() => handleRoleChange("Doctors")}>Doctors</div>
        </div>
      )}
    </div>
  );
};

export default Header;
